import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { C, LAYOUT } from "../constants/colors";
import { DT_STAGES } from "../constants/appConfig";
import { FONT } from "../theme/tokens";
import { FancyInput, FancySelect } from "./FormControls";
import { FormSection } from "./InputWizard";
import Btn from "./Btn";
import {
  parseProductStagePlan,
  spreadToEndStage,
} from "../utils/productStagePlan";

const STAGE_TONE = {
  empathize: "coral",
  define: "teal",
  ideate: "amber",
  prototype: "blue",
  test: "green",
  implement: "blue",
};

function stageMeta(key) {
  return DT_STAGES.find((s) => s.key === key) || DT_STAGES[0];
}

function nextBlock(rows, numDays, startStage) {
  const lastTo = rows[rows.length - 1]?.toDay || 0;
  const fromDay = Math.min(numDays, lastTo + 1);
  const toDay = Math.min(numDays, Math.max(fromDay, fromDay + 2));
  const used = new Set(rows.map((r) => r.stageKey));
  const nextStage =
    DT_STAGES.find((s) => !used.has(s.key))?.key ||
    startStage ||
    "empathize";
  return { fromDay, toDay, stageKey: nextStage, goal: "" };
}

export default function ProductStagePlanPanel({
  plan = [],
  onChange,
  numDays = 10,
  startStage = "empathize",
}) {
  const rows = parseProductStagePlan(plan);
  const lastKey = rows[rows.length - 1]?.stageKey || "prototype";
  const [finishStage, setFinishStage] = useState(lastKey);

  const setRows = (next) => onChange?.(parseProductStagePlan(next));
  const update = (i, patch) => {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const start = stageMeta(startStage);
  const finish = stageMeta(finishStage);

  return (
    <Box sx={{ mt: 2.5, fontFamily: FONT.family }}>
      <FormSection title="Product stage plan" icon="ti-flag-3" tone="teal">
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.muted, lineHeight: 1.5, mb: 2, letterSpacing: FONT.letterSpacing }}>
          Timing has <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>{numDays} days</Box>.
          Pick the finish stage, fill the calendar, or add your own day blocks. Generate keeps Project Build on that stage.
        </Typography>

        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: "10px",
            border: `1px solid ${C.border}`,
            background: "#F8FAFC",
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: "#fff",
                border: `1px solid ${finish.color}44`,
                display: "grid",
                placeItems: "center",
                color: finish.color,
                flexShrink: 0,
              }}
            >
              <i className={`ti ${finish.icon}`} style={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: C.muted, letterSpacing: "0.08em" }}>
                AUTO FILL
              </Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.text, letterSpacing: FONT.letterSpacing }}>
                Reach {finish.label} in {numDays} days
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: { sm: "auto" } }}>
              <Btn onClick={() => setRows(spreadToEndStage(numDays, startStage, finishStage))}>
                Fill {numDays} days
              </Btn>
            </Box>
          </Box>

          <FancySelect
            tone={STAGE_TONE[finishStage] || "teal"}
            icon={finish.icon}
            label="Finish stage"
            hint={`Begins at ${start.label} (selected above)`}
            value={finishStage}
            onChange={(e) => setFinishStage(e.target.value)}
            valueColor={finish.color}
          >
            {DT_STAGES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </FancySelect>
        </Box>

        <Box
          sx={{
            p: 1.75,
            borderRadius: "10px",
            border: `1px solid ${C.border}`,
            bgcolor: C.surface,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: C.muted, letterSpacing: "0.08em" }}>
                CUSTOM BLOCKS
              </Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.text, letterSpacing: FONT.letterSpacing }}>
                These days · this stage
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.muted, mt: 0.25, lineHeight: 1.45 }}>
                Example: days 1–4 Empathize, then 5–{numDays} Prototype.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Btn
                small
                variant="outline"
                onClick={() => setRows([...rows, nextBlock(rows, numDays, startStage)])}
              >
                + Add block
              </Btn>
              {rows.length ? (
                <Btn small variant="ghost" onClick={() => setRows([])}>
                  Clear
                </Btn>
              ) : null}
            </Box>
          </Box>

          {!rows.length ? (
            <Box
              sx={{
                py: 2,
                px: 1.5,
                borderRadius: `${LAYOUT.radiusSm}px`,
                bgcolor: C.surfaceAlt,
                border: `1.5px dashed ${C.border}`,
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
                No custom blocks. Use Fill above, or add a block.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.15 }}>
              {rows.map((r, i) => {
                const st = stageMeta(r.stageKey);
                return (
                  <Box
                    key={`${r.stageKey}-${i}`}
                    sx={{
                      p: 1.4,
                      borderRadius: `${LAYOUT.radius}px`,
                      border: `1.5px solid ${st.color}40`,
                      bgcolor: "#fff",
                      boxShadow: `0 6px 18px ${st.color}14`,
                      background: `linear-gradient(90deg, ${st.soft} 0%, #FFFFFF 42%)`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.1 }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "9px",
                          bgcolor: "#fff",
                          border: `1px solid ${st.color}55`,
                          display: "grid",
                          placeItems: "center",
                          color: st.color,
                          flexShrink: 0,
                        }}
                      >
                        <i className={`ti ${st.icon}`} style={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, letterSpacing: FONT.letterSpacing }}>
                        Block {i + 1}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.3,
                          borderRadius: 99,
                          bgcolor: st.soft,
                          color: st.color,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {st.label}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr minmax(180px, 1.4fr) auto" },
                        gap: 1,
                        alignItems: "end",
                      }}
                    >
                      <FancyInput
                        tone="blue"
                        icon="ti-calendar-event"
                        label="From day"
                        type="number"
                        min={1}
                        max={numDays}
                        value={r.fromDay}
                        onChange={(e) => update(i, { fromDay: Number(e.target.value) || 1 })}
                      />
                      <FancyInput
                        tone="blue"
                        icon="ti-calendar-event"
                        label="To day"
                        type="number"
                        min={1}
                        max={numDays}
                        value={r.toDay}
                        onChange={(e) => update(i, { toDay: Number(e.target.value) || r.fromDay })}
                      />
                      <FancySelect
                        tone={STAGE_TONE[r.stageKey] || "teal"}
                        icon={st.icon}
                        label="Stage"
                        value={r.stageKey}
                        onChange={(e) => update(i, { stageKey: e.target.value })}
                        valueColor={st.color}
                      >
                        {DT_STAGES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </FancySelect>
                      <Btn small variant="ghost" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}>
                        Remove
                      </Btn>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </FormSection>
    </Box>
  );
}
